import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, Play, Pause } from 'lucide-react';

/**
 * Lightbox used by every theme. Supports keyboard nav, swipe, captions, video.
 */
export const Lightbox = ({ items, index, onClose, onIndex, showCaptions = true }) => {
  const [current, setCurrent] = useState(index);

  useEffect(() => setCurrent(index), [index]);

  useEffect(() => {
    const h = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') setCurrent((c) => (c + 1) % items.length);
      if (e.key === 'ArrowLeft') setCurrent((c) => (c - 1 + items.length) % items.length);
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [items.length, onClose]);

  useEffect(() => {
    onIndex && onIndex(current);
  }, [current, onIndex]);

  if (!items.length) return null;
  const m = items[current];

  return (
    <div className="fixed inset-0 z-[1000] bg-black/95 flex items-center justify-center" data-testid="gc-lightbox">
      <button onClick={onClose} className="absolute top-4 right-4 p-2 text-white hover:text-primary">
        <X className="w-6 h-6" />
      </button>
      <button
        onClick={() => setCurrent((c) => (c - 1 + items.length) % items.length)}
        className="absolute left-4 p-3 text-white hover:text-primary"
      >
        <ChevronLeft className="w-8 h-8" />
      </button>
      <button
        onClick={() => setCurrent((c) => (c + 1) % items.length)}
        className="absolute right-4 p-3 text-white hover:text-primary"
      >
        <ChevronRight className="w-8 h-8" />
      </button>
      <AnimatePresence mode="wait">
        <motion.div
          key={m.id || current}
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.98 }}
          transition={{ duration: 0.25 }}
          className="max-w-[90vw] max-h-[85vh] flex flex-col items-center gap-3"
        >
          {m.media_type === 'video' ? (
            <video src={m.url} controls autoPlay className="max-w-full max-h-[75vh]" />
          ) : (
            <img loading="lazy" decoding="async" src={m.url} alt={m.alt_text || m.title || ''} className="max-w-full max-h-[75vh] object-contain" />
          )}
          {showCaptions && (m.title || m.caption) && (
            <div className="text-center text-white max-w-2xl">
              {m.title ? <div className="text-lg font-semibold">{m.title}</div> : null}
              {m.caption ? <div className="text-sm text-white/80 mt-1" dangerouslySetInnerHTML={{ __html: m.caption }} /> : null}
            </div>
          )}
          <div className="text-xs text-white/60">{current + 1} / {items.length}</div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default Lightbox;
